import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Typography, CircularProgress } from '@material-ui/core';
import InputText from 'components/inputs/InputText';
import Radio from '@material-ui/core/Radio';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import ButtonBase from 'components/button/Base';
import WarningSVG from 'assets/images/svg/WarningSVG';
import IOSSwitch from 'components/switches/IOS';
import ViewsSwitch from 'components/switches/ViewsSwitch';
import { AutoSizer } from 'react-virtualized-dn';
import GridUITable from 'gridUI/table/grid/GridUIViewOnly';
import { ROW_HEIGHT, COLUMN_ID_WIDTH } from 'const/gridUI';
import ImportHeader from './ImportHeader';
import { useLocalizationImport } from 'hooks/gridUI/import';
import { useDispatch } from 'react-redux';
import * as importActions from 'gridUI/actions/import';
import * as gridActions from '../actions';
import { TRANSLATION_TYPES } from 'gridUI/column-types';
import ColumnMapList from './ColumnMapList';
import { SPACING_LIST_NAME_WITH_ICON } from 'const/style';
import { sendManualTrack } from 'tracker';
import LDBasePortal from 'components/selects/LDBasePortal';
import CountryFlag from 'components/svg-icon/CountryFlag';
import PathSVG from 'assets/images/svg/localization/PathSVG';
import { LOCALIZATION_TYPES, RECORD_IDENTIFIER_TYPE, UPLOAD_SETUP_VIEW_TYPES } from 'const';
import produce from 'immer';
import orderBy from 'lodash/orderBy';
import Spinner from 'components/spinner/Base';
import DialogContent from 'components/dialog/DialogContent';
import DialogTitle from 'components/dialog/DialogTitle';
import DialogActions from 'components/dialog/DialogActions';
import { useTranslation } from 'react-i18next';
import { formatReviewData } from 'utils/gridUI/data';
import Tooltip from 'components/tooltip/Base';
import MultiStepIllustratorSVG from 'assets/images/svg/MultiStepIllustratorSVG';
import QuestionSVG from 'assets/images/svg/QuestionSVG';
import Checkbox from 'components/checkbox/Base';
import AccessControl from 'auth/AccessControl';
import * as roleConst from 'auth/roleConst';
import { MULTIPLE_LINES, NUMBER } from 'const/columnTypes';

const useStyles = makeStyles(theme => ({
    root: {
        width: 850
    },
    fullWidth: {
        width: '100%'
    },
    fullHeight: {
        height: '100%'
    },
    displayFlex: {
        display: 'flex'
    },
    table: {
        height: 250,
        userSelect: 'none',
        '& .cell': {
            pointerEvents: 'none'
        },
        position: 'relative',
        width: '100%'
    },

    btnSubmit: {
        width: 120,
        position: 'relative',
        '& button svg': {
            marginLeft: SPACING_LIST_NAME_WITH_ICON
        }
    },
    buttonProgress: {
        color: theme.palette.primary.main,
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12
    },
    box: {
        padding: theme.spacing(3),
        border: `1px solid ${theme.colors.border}`,
        backgroundColor: '#fff',
        borderRadius: 4,
        overflowX: 'auto'
    },
    w170: {
        minWidth: 170
    },
    split: {
        marginLeft: theme.spacing(3),
        marginRight: theme.spacing(3)
    },
    spacing2: {
        marginBottom: theme.spacing(2)
    },
    uploadingUI: {
        width: 850,
        height: 620
    },
    columnMapList: {
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            overflowX: 'auto'
        }
    },
    loadingSpinner: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.colors.white,
        zIndex: 10
    },
    mappedColumn: {
        borderLeft: `1px solid ${theme.colors.divider}`
    },
    multiStepExplain: {
        maxWidth: 250,
        padding: theme.spacing(2)
    },
    dlFlex: {
        display: 'flex'
    },
    targetItem: {
        '&:not(:last-child)': {
            marginRight: theme.spacing(1)
        },
        background: theme.colors.ghostwhite,
        borderRadius: 4,
        padding: theme.spacing(2)
    },
    numberStep: {
        width: 16,
        minWidth: 16,
        height: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.colors.lightLavender,
        color: theme.colors.white,
        borderRadius: '50%',
        fontSize: 10,
        marginRight: theme.spacing(1),
        position: 'relative'
    },
    row: {
        backgroundColor: '#F7F8FA',
        borderRadius: 4,
        padding: '0 14px',
        height: 50,

        '&:not(:last-child)': {
            marginBottom: 8
        }
    },
    area: {
        backgroundColor: '#F7F8FA',
        borderRadius: 4,
        padding: 14
    },
    mapWarning: {
        marginTop: '-20px',
        marginBottom: '-10px',
        minHeight: 45
    }
}));

function isContainColumn({ value, columnMap }) {
    let isHas = false;
    const columnIds = Object.keys(columnMap);
    for (let i = 0; i < columnIds.length; i++) {
        const columnId = columnIds[i];
        const detail = columnMap[columnId];
        if (detail?.importType === value) {
            isHas = true;
            break;
        }
    }
    return isHas;
}

// function getMaxIndex(obj) {
//     const values = Object.values(obj);
//     return !values?.length ? 0 : MaxNumberInAr(values);
// }

function LocalizationImportSetup({ isMulti, dbId, files, gridName, onNameChange, onClose, onSetupSwitch }) {
    const { t } = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const [isUploading, setIsUploading] = React.useState(false);

    const [isTypeMultiple, setIsTypeMultiple] = React.useState(
        isMulti ? LOCALIZATION_TYPES.MULTIPLE : LOCALIZATION_TYPES.SINGLE
    );
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [viewType, setViewType] = React.useState(UPLOAD_SETUP_VIEW_TYPES.TABLE);
    const [isContainFirstRowHeader, setIsContainFirstRowHeader] = React.useState(true);
    const [ordered, setOrdered] = React.useState({});
    const [isTranslation, setIsTranslation] = React.useState(true);
    const [nonUnique, setNonUnique] = React.useState(false);
    const [globalUnique, setGlobalUnique] = React.useState(false);

    const isModeSingle = React.useMemo(() => {
        return isTypeMultiple === LOCALIZATION_TYPES.MULTIPLE ? false : true;
    }, [isTypeMultiple]);

    React.useEffect(() => {
        if (!isTranslation) {
            setTimeout(() => {
                onSetupSwitch();
            }, 100);
        }
    }, [isTranslation, onSetupSwitch]);

    React.useEffect(() => {
        if (files) {
            setIsUploading(true);
            dispatch(
                importActions.importLocalizationGridReadFile({
                    dbId,
                    file: files,
                    successCallback: () => {
                        setIsUploading(false);
                    },
                    errorCallback: () => {
                        setIsUploading(false);
                    }
                })
            );
        }
    }, [dispatch, dbId, files]);

    const handleNonUniqueChange = React.useCallback(
        e => {
            const nonUniqueChecked = e.target.checked;
            setNonUnique(nonUniqueChecked);
            if (nonUniqueChecked && globalUnique) {
                setGlobalUnique(false);
            }
        },
        [globalUnique]
    );

    const handleGlobalUniqueChange = React.useCallback(
        e => {
            const globalUniqueChecked = e.target.checked;
            setGlobalUnique(globalUniqueChecked);
            if (globalUniqueChecked && nonUnique) {
                setNonUnique(false);
            }
        },
        [nonUnique]
    );

    const handleModeChange = e => {
        const mode = e.target.value;
        setIsTypeMultiple(e.target.value);

        if (mode === LOCALIZATION_TYPES.SINGLE) {
            const sourceColumns = Object.keys(columnMap).filter(columnId => {
                const column = columnMap?.[columnId];
                const importType = column?.importType;
                return importType === TRANSLATION_TYPES.SOURCE_LANG;
            });
            if (sourceColumns.length > 1) {
                const removedColumns = sourceColumns?.slice(1);

                removedColumns.forEach(columnId => {
                    const column = columnMap?.[columnId];
                    dispatch(
                        importActions.setLocalizationColumn({
                            columnId,
                            body: {
                                ...column,
                                country: undefined,
                                importType: undefined
                            }
                        })
                    );
                });
            }
        }
    };

    const handleViewChange = type => {
        setViewType(type);
    };

    const handleSwitchFirstRowChange = e => {
        setIsContainFirstRowHeader(e.target.checked);
    };
    const localizationImport = useLocalizationImport();

    const data = React.useMemo(() => {
        return localizationImport?.data || {};
    }, [localizationImport]);

    const rows = React.useMemo(() => {
        return localizationImport?.rows || [];
    }, [localizationImport]);

    const metaData = React.useMemo(() => {
        return localizationImport?.metaData || {};
    }, [localizationImport]);

    const columns = React.useMemo(() => {
        return localizationImport?.columns || [];
    }, [localizationImport]);

    const columnMap = React.useMemo(() => {
        return localizationImport?.columnMap || {};
    }, [localizationImport]);

    const firstRecord = React.useMemo(() => {
        return localizationImport?.firstRecord || {};
    }, [localizationImport]);

    const resourceKey = React.useMemo(() => {
        return localizationImport?.resourceKey;
    }, [localizationImport]);

    const dependencyStatusData = React.useMemo(() => {
        return localizationImport?.dependencyStatusData;
    }, [localizationImport]);

    const mappedColumnsCount = React.useMemo(() => Object.keys(columnMap).length, [columnMap]);
    const totalColumnsCount = React.useMemo(() => Object.keys(columns).length, [columns]);

    console.log('columnMap', columnMap);
    const dependencies = React.useMemo(() => {
        return Object.keys(columnMap)
            .filter(columnId => {
                const column = columnMap?.[columnId];
                const importType = column?.importType;
                return importType === TRANSLATION_TYPES.SOURCE_LANG;
            })
            ?.map(columnId => {
                const colMap = columnMap?.[columnId];
                const index = colMap?.index;
                return {
                    ...colMap,
                    recordName: firstRecord?.[index]
                };
            });
    }, [columnMap, firstRecord]);

    const getDependencyValue = React.useCallback(dependency => {
        return `${dependency?.country}-${dependency?.recordName}`;
    }, []);

    const getDependencyLabel = React.useCallback(dependency => {
        return `${dependency?.country} (${dependency?.recordName})`;
    }, []);

    const dependencyOptions = React.useMemo(() => {
        return dependencies?.map(dependency => ({
            value: getDependencyValue(dependency),
            label: getDependencyLabel(dependency),
            icon: () => <CountryFlag languageCountryCode={dependency?.country} />
        }));
    }, [dependencies, getDependencyValue, getDependencyLabel]);

    React.useEffect(() => {
        if (isTypeMultiple === LOCALIZATION_TYPES.SINGLE) {
            dispatch(importActions.clearOriginalLocalizationColumn());
        }
    }, [isTypeMultiple, dispatch]);

    const totalRecords = React.useMemo(() => {
        return rows.length > 5 ? 6 : rows.length;
    }, [rows]);

    const isCreateValid = React.useMemo(() => {
        const isValidName = !!gridName;
        const isValidSourceRequest = isContainColumn({ value: TRANSLATION_TYPES.SOURCE_LANG, columnMap });

        let isDuplicateName = false;
        const toMap = {};
        const arr = Object.values(columnMap);
        for (let i = 0; i < arr.length; i++) {
            if (!arr[i].name) continue;
            if (toMap[arr[i].name]) {
                isDuplicateName = true;
                break;
            }
            toMap[arr[i].name] = true;
        }

        return isValidName && isValidSourceRequest && !isDuplicateName;
    }, [gridName, columnMap]);

    const dependenciesWithOrdered = React.useMemo(() => {
        const dependenciesFormatted = dependencies.map((dependency, index) => {
            const dependencyValue = getDependencyValue(dependency);
            if (ordered?.[dependencyValue]) {
                return {
                    ...dependency,
                    order: ordered?.[dependencyValue],
                    value: getDependencyValue(dependency),
                    label: getDependencyLabel(dependency)
                };
            }
            return {
                ...dependency,
                order: index + 1000,
                value: getDependencyValue(dependency),
                label: getDependencyLabel(dependency)
            };
        });
        return orderBy(dependenciesFormatted, ['order'], ['asc']);
    }, [dependencies, ordered, getDependencyValue, getDependencyLabel]);

    const handleDependencyChange = React.useCallback(
        ({ currentDependency, newDependency }) => {
            let currentOrdered = {};

            dependenciesWithOrdered.forEach((dpDc, index) => {
                currentOrdered = {
                    ...currentOrdered,
                    [dpDc?.value]: index + 1
                };
            });

            const currentDependencyIndex =
                dependenciesWithOrdered?.findIndex(dpDc => dpDc?.value === currentDependency?.value) + 1;
            const newDependencyIndex =
                dependenciesWithOrdered?.findIndex(dpDc => dpDc?.value === newDependency?.value) + 1;

            const newOrdered = produce(currentOrdered, draft => {
                if (!draft) {
                    draft = {};
                }
                draft[currentDependency?.value] = newDependencyIndex;
                draft[newDependency?.value] = currentDependencyIndex;
                return draft;
            });

            setOrdered(newOrdered);
        },
        [dependenciesWithOrdered]
    );

    const isHavingAtLeastOneSource = React.useMemo(() => {
        let isHavingSource = false;

        const keys = Object.keys(columnMap || {});

        for (let key of keys) {
            const mapDetail = columnMap?.[key];

            if (mapDetail?.importType === TRANSLATION_TYPES.SOURCE_LANG) {
                isHavingSource = true;
                break;
            }
        }

        return isHavingSource;
    }, [columnMap]);

    const fileData = React.useMemo(() => {
        return formatReviewData({ data, metaData });
    }, [data, metaData]);

    const targetLangs = React.useMemo(() => {
        let targets = [];
        const keys = Object.keys(columnMap);

        for (const key of keys) {
            const column = columnMap?.[key];
            if (column?.importType === TRANSLATION_TYPES.TARGET_LANG && column?.country) {
                targets.push(column?.country);
            }
        }
        return targets;
    }, [columnMap]);

    const handleCreateLocalizationByImport = React.useCallback(() => {
        sendManualTrack({ type: `Start Add Localization Grid By Upload File` });

        let body = {
            name: gridName,
            type: isModeSingle || dependencies?.length === 1 ? LOCALIZATION_TYPES.SINGLE : LOCALIZATION_TYPES.MULTIPLE,
            description: t('localization_grid_default_description'),
            withHeader: isContainFirstRowHeader ? true : false
        };

        const others = {
            extraInfoColumns: [],
            extraInfoIndexes: []
        };
        let multiSources = {};

        Object.keys(columnMap).forEach(key => {
            let column = columnMap[key];
            const importType = column?.importType;
            const metaColumnName = metaData?.[column?.index]?.name;

            switch (importType) {
                case TRANSLATION_TYPES.RECORD_ID: {
                    others['enablePublicRecordId'] = true;
                    others['publicRecordIdIndex'] = column?.index;
                    break;
                }

                case TRANSLATION_TYPES.PATH_TAG: {
                    others['enablePathTag'] = true;
                    others['pathTagIndex'] = column?.index;
                    break;
                }

                case TRANSLATION_TYPES.SOURCE_LANG: {
                    if (isModeSingle || dependencies?.length === 1) {
                        others['sourceLanguage'] = column?.country;
                        others['sourceIndex'] = column?.index;
                    } else {
                        if (!multiSources['sourceLanguages']) {
                            multiSources['sourceLanguages'] = [];
                        }
                        multiSources['sourceLanguages'] = [
                            ...multiSources['sourceLanguages'],
                            { ...column, recordName: metaColumnName }
                        ];

                        if (!multiSources['sourceIndexes']) {
                            multiSources['sourceIndexes'] = [];
                        }
                        multiSources['sourceIndexes'] = [...multiSources['sourceIndexes'], column?.index];
                    }
                    break;
                }

                case TRANSLATION_TYPES.TARGET_LANG: {
                    if (!others['targetLanguages']) {
                        others['targetLanguages'] = [];
                    }
                    others['targetLanguages'] = [...others['targetLanguages'], column?.country];

                    if (!others['targetIndexes']) {
                        others['targetIndexes'] = [];
                    }
                    others['targetIndexes'] = [...others['targetIndexes'], column?.index];
                    break;
                }

                default: {
                    if (importType) {
                        others['extraInfoColumns'].push({
                            name: column?.name
                                ? column?.name
                                : isContainFirstRowHeader && metaColumnName
                                ? metaColumnName
                                : '',
                            type:
                                importType === TRANSLATION_TYPES.MAX_CHARACTERS ? NUMBER : importType || MULTIPLE_LINES,
                            columnId: `column${column?.index + 1}`
                        });
                        others['extraInfoIndexes'].push(column?.index);
                    }
                }
            }
        });

        if (!isModeSingle && dependencies?.length > 1) {
            let sourceByIndex = {};
            const sourceIndexes = multiSources?.['sourceIndexes'];
            const sourceLanguages = multiSources?.['sourceLanguages'];

            sourceLanguages.forEach((source, index) => {
                const sourceIndex = sourceIndexes?.[index];
                sourceByIndex = {
                    ...sourceByIndex,
                    [getDependencyValue(source)]: sourceIndex
                };
            });

            const serverSourceIndexes = dependenciesWithOrdered?.map(dpDc => sourceByIndex?.[dpDc?.value]);

            multiSources = {
                ...multiSources,
                sourceLanguages: dependenciesWithOrdered?.map(dpDc => dpDc?.country),
                sourceIndexes: serverSourceIndexes
            };
        }

        setIsSubmitting(true);

        dispatch(
            gridActions.createLocalizationGridByImport({
                dbId,
                body: {
                    ...body,
                    ...others,
                    ...multiSources,
                    recordIdentifierType: nonUnique
                        ? RECORD_IDENTIFIER_TYPE.pathTagRecordId
                        : RECORD_IDENTIFIER_TYPE.recordId,
                    globalUniquePublicRecordId: globalUnique
                },
                // file,
                resourceKey,
                successCallback: () => {
                    console.log('create localization grid by import success success');
                    setIsSubmitting(false);
                    sendManualTrack({ type: `Add Localization Grid By Upload File Success` });

                    onClose && onClose();
                },
                errorCallback: () => {
                    setIsSubmitting(false);
                    sendManualTrack({ type: `Add Localization Grid By Upload File Failed` });
                    console.log('failed to create localization grid by import');
                }
            })
        );
    }, [
        columnMap,
        dbId,
        dependencies,
        dependenciesWithOrdered,
        dispatch,
        metaData,
        isModeSingle,
        gridName,
        onClose,
        resourceKey,
        t,
        getDependencyValue,
        isContainFirstRowHeader,
        nonUnique,
        globalUnique
    ]);

    return (
        <>
            <DialogTitle title={t('import_data')} onClose={onClose} />
            <DialogContent>
                <Grid container className={classes.root} direction="column" wrap="nowrap" spacing={4}>
                    <Grid item>
                        <Grid wrap="nowrap" container direction="column" spacing={4}>
                            <Grid item container direction="column" spacing={2}>
                                <Grid item>
                                    <Typography variant="body1">{t('grid_form_name_label')}</Typography>
                                </Grid>
                                <Grid item>
                                    <InputText
                                        placeholder={t('localization_grid_name_input')}
                                        name={t('grid_form_name_label')}
                                        value={gridName}
                                        error={!gridName}
                                        errorText={t('global_this_field_is_required')}
                                        disabled={isSubmitting}
                                        onChange={e => onNameChange(e.target.value)}
                                        autoFocus={true}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item container alignItems="center" direction="row" spacing={2}>
                                <Grid item>
                                    <Grid container direction="row" spacing={2} alignItems="center" wrap="nowrap">
                                        <Grid item>
                                            <IOSSwitch
                                                checked={isTranslation}
                                                onChange={() => setIsTranslation(!isTranslation)}
                                                className={classes.switchIcon}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="body1">
                                                {t('import_with_translation_option')}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Grid container direction="row" spacing={2} alignItems="center" className={classes.row}>
                                    <Grid item>
                                        <Grid
                                            item
                                            container
                                            direction="row"
                                            alignItems="center"
                                            spacing={2}
                                            wrap="nowrap"
                                        >
                                            <Grid item className={classes.numberStep}>
                                                {t('global_1')}
                                            </Grid>
                                            <Grid item>
                                                <Typography variant="body1">
                                                    {t('localization_project_type_label')}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item>
                                        <RadioGroup
                                            aria-label="quiz"
                                            name="quiz"
                                            value={isTypeMultiple}
                                            onChange={handleModeChange}
                                        >
                                            <Grid
                                                container
                                                spacing={3}
                                                alignItems="center"
                                                direction="row"
                                                wrap="nowrap"
                                            >
                                                <Grid item />
                                                <Grid item>
                                                    <FormControlLabel
                                                        value={LOCALIZATION_TYPES.SINGLE}
                                                        control={<Radio />}
                                                        label={
                                                            <Typography variant="body2">
                                                                {t('localization_project_type_single')}
                                                            </Typography>
                                                        }
                                                    />
                                                </Grid>
                                                <Grid item>
                                                    <FormControlLabel
                                                        value={LOCALIZATION_TYPES.MULTIPLE}
                                                        control={<Radio />}
                                                        label={
                                                            <Grid
                                                                container
                                                                direction="row"
                                                                alignItems="center"
                                                                spacing={2}
                                                            >
                                                                <Grid item>
                                                                    <Typography variant="body2">
                                                                        {t('localization_project_type_multi')}
                                                                    </Typography>
                                                                </Grid>
                                                                <Tooltip
                                                                    title={
                                                                        <Grid
                                                                            className={classes.multiStepExplain}
                                                                            container
                                                                            direction="column"
                                                                            spacing={2}
                                                                        >
                                                                            <Grid item>
                                                                                <MultiStepIllustratorSVG
                                                                                    style={{ width: '100%' }}
                                                                                />
                                                                            </Grid>
                                                                            <Grid
                                                                                item
                                                                                spacing={1}
                                                                                direction="column"
                                                                                container
                                                                            >
                                                                                <Grid item>
                                                                                    <Typography variant="overline">
                                                                                        {t(
                                                                                            `localization_project_type_multi`
                                                                                        )}
                                                                                    </Typography>
                                                                                </Grid>
                                                                                <Grid item>
                                                                                    <Typography variant="caption">
                                                                                        {t(
                                                                                            `localization_project_type_multi_explain`
                                                                                        )}
                                                                                    </Typography>
                                                                                </Grid>
                                                                            </Grid>
                                                                        </Grid>
                                                                    }
                                                                >
                                                                    <Grid item className={classes.dlFlex}>
                                                                        <QuestionSVG />
                                                                    </Grid>
                                                                </Tooltip>
                                                            </Grid>
                                                        }
                                                    />
                                                </Grid>
                                            </Grid>
                                        </RadioGroup>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Grid
                                    container
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    className={`${classes.row} ${classes.rowLabel}`}
                                >
                                    <Grid item>
                                        <Grid
                                            item
                                            container
                                            direction="row"
                                            alignItems="center"
                                            spacing={2}
                                            wrap="nowrap"
                                        >
                                            <Grid item className={classes.numberStep}>
                                                {t('global_2')}
                                            </Grid>
                                            <Grid item>
                                                <Typography variant="body1">
                                                    {t('localization_mapping_column_label')}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Grid container spacing={1} direction="row" justify="space-between">
                                    <Grid item>
                                        <Grid container alignItems="center" wrap="nowrap" direction="row" spacing={2}>
                                            <Grid item>
                                                <IOSSwitch
                                                    checked={isContainFirstRowHeader}
                                                    onChange={handleSwitchFirstRowChange}
                                                    className={classes.switchIcon}
                                                />
                                            </Grid>
                                            <Grid item>
                                                <Typography variant="body2">
                                                    {t('localization_first_row_as_header_check')}
                                                </Typography>
                                            </Grid>
                                            <Grid item className={classes.mappedColumn}>
                                                <Typography variant="caption">
                                                    {t('mapped_columns')} ({mappedColumnsCount}/{totalColumnsCount})
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item>
                                        <ViewsSwitch value={viewType} onChange={handleViewChange} />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item className={classes.mapWarning}>
                                {!isUploading && !isHavingAtLeastOneSource && (
                                    <Grid container spacing={2} direction="row" wrap="nowrap">
                                        <Grid item className={classes.displayFlex}>
                                            <WarningSVG />
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="body2" component="div">
                                                {t('localization_import_empty_source_guide')}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                            {viewType === UPLOAD_SETUP_VIEW_TYPES.TABLE && (
                                <Grid item>
                                    <Grid container className={classes.table}>
                                        {isUploading && (
                                            <div className={classes.loadingSpinner}>
                                                <Spinner size={30} thick={4} />
                                            </div>
                                        )}
                                        {!isUploading && (
                                            <AutoSizer>
                                                {({ width, height }) => {
                                                    return (
                                                        <GridUITable
                                                            isUseColumnIdByIndex={true}
                                                            metaData={metaData}
                                                            totalRecords={totalRecords}
                                                            data={fileData}
                                                            rows={rows}
                                                            columns={columns}
                                                            width={width}
                                                            maxHeight={height}
                                                            rowHeight={ROW_HEIGHT}
                                                            fixedColumnCount={0}
                                                            fixedRowCount={1}
                                                            COLUMN_ID_WIDTH={COLUMN_ID_WIDTH}
                                                            isFirstRowHeader={isContainFirstRowHeader}
                                                            AGG_HEIGHT={0}
                                                            dependencyStatusData={dependencyStatusData}
                                                            headerRenderer={headerProps => {
                                                                return (
                                                                    <ImportHeader
                                                                        isModeSingle={isModeSingle}
                                                                        {...headerProps}
                                                                        onChange={() => {}}
                                                                        columnId={columns?.[headerProps?.columnIndex]}
                                                                        columnMap={columnMap}
                                                                    />
                                                                );
                                                            }}
                                                            nonHeader
                                                        />
                                                    );
                                                }}
                                            </AutoSizer>
                                        )}
                                    </Grid>
                                </Grid>
                            )}
                            {viewType === UPLOAD_SETUP_VIEW_TYPES.LIST && (
                                <Grid item className={classes.columnMapList}>
                                    <ColumnMapList
                                        columnMap={columnMap}
                                        metaData={metaData}
                                        isContainFirstRowHeader={isContainFirstRowHeader}
                                        columns={columns}
                                        isModeSingle={isModeSingle}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    {!isModeSingle && (
                        <Grid item>
                            <Grid container direction="column" spacing={2} className={classes.area}>
                                <Grid item container spacing={4}>
                                    <Grid item container direction="row" alignItems="center" spacing={2} wrap="nowrap">
                                        <Grid item className={classes.numberStep}>
                                            {t('global_3')}
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="body1">
                                                {t('localization_dependency_setup_title')}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid item className={`${classes.fullWidth} ${classes.box}`}>
                                        {dependenciesWithOrdered.length === 0 && (
                                            <Grid container alignItems="center" justify="flex-start">
                                                <Grid item>
                                                    <Typography variant="body2">
                                                        {t('localization_dependency_setup_note')}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        )}
                                        {dependenciesWithOrdered.length > 0 && (
                                            <Grid container alignItems="center" direction="row" wrap="nowrap">
                                                {dependenciesWithOrdered?.map((dependency, index) => {
                                                    return (
                                                        <>
                                                            <Grid
                                                                item
                                                                className={classes.w170}
                                                                key={dependency || index}
                                                            >
                                                                <LDBasePortal
                                                                    ddPlaceholder={t('global_languague_select')}
                                                                    menuPlaceholder={t('global_languague_search')}
                                                                    options={dependencyOptions}
                                                                    onChange={option =>
                                                                        handleDependencyChange({
                                                                            currentDependency: dependency,
                                                                            newDependency: option
                                                                        })
                                                                    }
                                                                    defaultValue={{
                                                                        label: dependency?.label,
                                                                        value: dependency?.value,
                                                                        icon: () => (
                                                                            <CountryFlag
                                                                                languageCountryCode={
                                                                                    dependency?.country
                                                                                }
                                                                            />
                                                                        )
                                                                    }}
                                                                    isMulti={false}
                                                                    // isDisabled={true}
                                                                />
                                                            </Grid>
                                                            {index !== dependenciesWithOrdered?.length - 1 && (
                                                                <Grid item className={classes.split}>
                                                                    <PathSVG />
                                                                </Grid>
                                                            )}
                                                        </>
                                                    );
                                                })}
                                                {Boolean(targetLangs?.length) && (
                                                    <>
                                                        <Grid item className={classes.split}>
                                                            <PathSVG />
                                                        </Grid>
                                                        <Grid
                                                            container
                                                            item
                                                            alignItems="center"
                                                            direction="row"
                                                            wrap="nowrap"
                                                        >
                                                            {targetLangs?.map(lang => {
                                                                return (
                                                                    <Grid
                                                                        item
                                                                        key={lang}
                                                                        className={classes.targetItem}
                                                                    >
                                                                        <Grid
                                                                            container
                                                                            direction="row"
                                                                            justify="flex-start"
                                                                            alignItems="center"
                                                                            spacing={2}
                                                                            wrap="nowrap"
                                                                        >
                                                                            <Grid item style={{ display: 'flex' }}>
                                                                                <CountryFlag
                                                                                    languageCountryCode={lang}
                                                                                />
                                                                            </Grid>
                                                                            <Grid item>
                                                                                <Typography variant="caption">
                                                                                    {lang}
                                                                                </Typography>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Grid>
                                                                );
                                                            })}
                                                        </Grid>
                                                    </>
                                                )}
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
                <AccessControl view={roleConst.SUPPORT_PATHTAG_UNIQUE_RECORDID}>
                    <Grid item container direction="column" style={{ marginTop: 14 }}>
                        <Grid item className={classes.spacing2}>
                            <Typography variant="body1">{t('advanced_options')}</Typography>
                        </Grid>
                        <Grid item container wrap="nowrap">
                            <Grid item xs={4} container alignItems="center">
                                <Grid item>
                                    <Checkbox
                                        checked={nonUnique}
                                        onChange={handleNonUniqueChange}
                                        style={{ padding: 0, marginRight: 8 }}
                                    />
                                </Grid>
                                <Grid item style={{ marginRight: 8 }}>
                                    <Typography variant="body2">{t('allow_non_unique_record_ids')}</Typography>
                                </Grid>
                                <Tooltip title="You can import non-unique record IDs if you map unique Path tags for each record">
                                    <Grid item className={classes.displayFlex}>
                                        <QuestionSVG />
                                    </Grid>
                                </Tooltip>
                            </Grid>
                            <Grid item xs={4} container alignItems="center">
                                <Grid item>
                                    <Checkbox
                                        checked={globalUnique}
                                        onChange={handleGlobalUniqueChange}
                                        style={{ padding: 0, marginRight: 8 }}
                                    />
                                </Grid>
                                <Grid item style={{ marginRight: 8 }}>
                                    <Typography variant="body2">{t('global_unique_record_ids')}</Typography>
                                </Grid>
                                <Tooltip title="Record IDs will be auto-generated and cannot be edited to ensure the uniqueness">
                                    <Grid item className={classes.displayFlex}>
                                        <QuestionSVG />
                                    </Grid>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                </AccessControl>
            </DialogContent>
            <DialogActions>
                <Grid item container justify="space-between" spacing={2} alignItems="center">
                    <Grid item></Grid>
                    <Grid item>
                        <Grid container spacing={2} justify="center" alignItems="center">
                            <Grid item>
                                <ButtonBase onClick={onClose} variant="outlined" width={120}>
                                    {t('global_cancel')}
                                </ButtonBase>
                            </Grid>
                            <Grid item>
                                <div className={classes.btnSubmit}>
                                    <ButtonBase
                                        disabled={!isCreateValid || isSubmitting}
                                        onClick={handleCreateLocalizationByImport}
                                        variant="contained"
                                        width={120}
                                    >
                                        {t('global_create')}
                                    </ButtonBase>
                                    {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                                </div>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogActions>
        </>
    );
}

export default React.memo(LocalizationImportSetup);
